import { analyzeMonth, COST_GROUPS, shortLine, type CostImportReport, type MarginImportReport, type MarginImportRow, type MarginOverview, type MarginProject, type MarginValues, type Me } from '@ft/shared';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loading, useLoad } from '../components/ui';
import { getLang, useI18n, type T } from '../i18n';
import { figuresOf, GROUP_COLOR, groupTotals, SEV_TONE } from '../lib/analysis';
import { canEdit as canEditArea, getAreaName } from '../lib/area';
import { upload } from '../lib/download';
import { dtfmt, eur } from '../lib/format';
import { api } from '../lib/http';
import { Chip, CollapseAll, Panel } from '../lib/collapse';

/**
 * Marge je Projekt aus dem Power-BI-Export (Workday „Project Margin“): Kennzahlen, Monate, Auffälligkeiten,
 * Projekte mit Status gegen eine Zielmarge und je Kunde. Nur Projekte mit Umsatz; Monate ohne Umsatz gelten als offen.
 */
type Status = 'ok' | 'watch' | 'crit';

const marginPct = (v: MarginValues) => (v.netSalesCents ? ((v.netSalesCents - v.costCents) / v.netSalesCents) * 100 : null);
const p1 = (v: number) => `${v.toLocaleString(getLang() === 'en' ? 'en-GB' : 'de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}${getLang() === 'en' ? '%' : ' %'}`;
const p0 = (v: number) => `${Math.round(v)}${getLang() === 'en' ? '%' : ' %'}`;
const hrs = (x100: number) => Math.round(x100 / 100).toLocaleString(getLang() === 'en' ? 'en-GB' : 'de-DE');
const monthName = (i: number, style: 'short' | 'long' = 'short') => new Date(Date.UTC(2026, i, 1)).toLocaleString(getLang() === 'en' ? 'en-GB' : 'de-DE', { month: style, timeZone: 'UTC' });
const ym = (s: string) => { const [y, m] = s.split('-'); return `${monthName(Number(m) - 1)} ${y}`; };
const statusOf = (p: number | null, target: number): Status => (p === null || p < target - 10 ? 'crit' : p < target ? 'watch' : 'ok');
const TONE: Record<Status, string> = { ok: 'pos', watch: 'crit', crit: 'neg' };
const COLOR: Record<Status, string> = { ok: 'var(--pos)', watch: 'var(--crit)', crit: 'var(--neg)' };

export function MarginPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const [sp, setSp] = useSearchParams();
  const year = sp.get('jahr');
  const { data, error, reload } = useLoad<MarginOverview>(`/api/margin${year ? `?year=${year}` : ''}`);
  const [filter, setFilter] = useState<'all' | Status>('all');
  const [importing, setImporting] = useState(false);
  const [costImporting, setCostImporting] = useState(false);
  const edit = canEditArea(me);

  const target = data?.areaTarget ?? 45;
  const view = useMemo(() => {
    if (!data) return null;
    const closed = new Set(data.closedMonths);
    const lastClosed = data.closedMonths.length ? Math.max(...data.closedMonths) : 0;
    const rows = data.projects.map((p) => {
      const pct = marginPct(p);
      const series = p.months.slice(0, lastClosed).map((v, i) => (closed.has(i + 1) && v.netSalesCents ? marginPct(v) : null));
      const vals = series.filter((v): v is number => v !== null);
      const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      const last = vals.length ? vals[vals.length - 1]! : null;
      const goal = p.target ?? target;
      // Kostenmix über die abgeschlossenen Monate und Kurzanalyse des letzten Monats gegen den Vormonat
      const figures = figuresOf(p);
      const closedIdx = [...closed].map((m) => m - 1);
      const mix = p.costMonths ? groupTotals(figures, closedIdx) : null;
      const analysis = lastClosed > 0 ? analyzeMonth(figures, lastClosed - 1, goal / 100) : null;
      return { p, pct, goal, status: statusOf(pct, goal), series, falling: last !== null && vals.length > 3 && last <= avg - 8, last, avg, mix, analysis };
    });
    return { closed, lastClosed, rows };
  }, [data, target]);

  const findings = useMemo(() => {
    if (!view) return [];
    const out: { level: 'urgent' | 'check'; text: string }[] = [];
    for (const r of view.rows) {
      const lastIdx = r.series.reduce((a: number, v, i) => (v !== null ? i : a), -1);
      const last = r.last;
      if (last !== null && last < 0) out.push({ level: 'urgent', text: t('mg.find.negative', { p: r.p.name, m: monthName(lastIdx, 'long'), v: p0(last) }) });
      else if (r.falling && last !== null) out.push({ level: 'check', text: t('mg.find.falling', { p: r.p.name, v: p0(last), m: monthName(lastIdx, 'long'), avg: p0(r.avg) }) });
      else if (r.pct !== null && r.pct < r.goal - 10) out.push({ level: 'check', text: t('mg.find.below', { p: r.p.name, v: p1(r.pct), g: p0(r.goal) }) });
    }
    return out.sort((a, b) => (a.level === b.level ? 0 : a.level === 'urgent' ? -1 : 1)).slice(0, 6);
  }, [view, target, t]);

  const setYear = (y: string) => { const n = new URLSearchParams(sp); n.set('jahr', y); setSp(n, { replace: true }); };

  return (
    <section className="page wide">
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('mg.title')}</h1></div>
        <span className="spacer" />
        {data && data.projects.length > 0 && <CollapseAll ids={['mg.kpis', 'mg.months', 'mg.find', 'mg.cust']} />}
        {data && data.years.length > 1 && (
          <div className="fld"><label htmlFor="mg-y">{t('mg.year')}</label>
            <select id="mg-y" value={data.year} onChange={(e) => setYear(e.target.value)}>{data.years.map((y) => <option key={y}>{y}</option>)}</select>
          </div>
        )}
        {data && (
          <div className="fld"><span className="flabel">{t('mg.areaTarget')}</span>
            <TargetEdit value={data.areaTargetSet ? data.areaTarget : null} fallback={45} canEdit={data.canSetAreaTarget} label={t('mg.areaTarget')} id="area"
              onSave={async (pct) => { await api('PUT', '/api/margin/targets', { workdayProjectId: null, pct }); reload(); }} />
          </div>
        )}
        {edit && <button className="btn" onClick={() => { setImporting(true); setCostImporting(false); }}>{t('mg.import')}</button>}
        {edit && <button className="btn primary" onClick={() => { setCostImporting(true); setImporting(false); }}>{t('mg.costImport')}</button>}
      </div>
      <p className="muted lead">{t('mg.lead')}</p>
      {data?.lastImport && <p className="muted small" style={{ margin: 0 }}>{t('mg.last', { at: dtfmt(data.lastImport.at), by: data.lastImport.by ?? '–', file: data.lastImport.file, m: data.lastImport.lastMonth ? ym(data.lastImport.lastMonth) : '–' })}</p>}

      {data?.lastCostImport && (
        <div className="box mgcostinfo">
          <div><div className="lbl">{t('mg.ci.last')}</div><div>{t('mg.ci.lastLine', { at: dtfmt(data.lastCostImport.at), by: data.lastCostImport.by ?? '–', file: data.lastCostImport.file, from: data.lastCostImport.firstMonth ? ym(data.lastCostImport.firstMonth) : '–', to: data.lastCostImport.lastMonth ? ym(data.lastCostImport.lastMonth) : '–' })}</div></div>
          <span className="pill pos">{t('mg.ci.taken', { n: data.lastCostImport.projectsCount })}</span>
          {data.lastCostImport.skipped.length > 0 && <>
            <span className="pill neu">{t('mg.ci.skippedN', { n: data.lastCostImport.skipped.length })}</span>
            <span className="muted small mgcostinfo-list">{t('mg.ci.skippedList', { list: data.lastCostImport.skipped.map((x) => `${x.workdayProjectId} ${x.label}`).join(', '), sum: eur(data.lastCostImport.skipped.reduce((a, x) => a + x.totalCents, 0)) })}</span>
          </>}
        </div>
      )}

      {importing && <ImportPanel onClose={() => setImporting(false)} onApplied={reload} />}
      {costImporting && <CostImportPanel onClose={() => setCostImporting(false)} onApplied={reload} />}

      {!data || !view ? <Loading error={error} /> : !data.projects.length ? (
        <div className="box empty">{data.lastImport ? t('mg.emptyYear', { y: data.year }) : t('mg.empty')}</div>
      ) : (
        <>
          <Kpis data={data} target={target} t={t} />
          <div className="grid2 mggrid">
            <MonthChart data={data} target={target} t={t} />
            <Panel id="mg.find" title={t('mg.find')} summary={<>{findings.filter((f) => f.level === 'urgent').length > 0 && <Chip tone="neg"><b>{findings.filter((f) => f.level === 'urgent').length}</b> {t('mg.lvl.urgent')}</Chip>}{findings.filter((f) => f.level !== 'urgent').length > 0 && <Chip tone="crit"><b>{findings.filter((f) => f.level !== 'urgent').length}</b> {t('mg.lvl.check')}</Chip>}{!findings.length && <Chip tone="pos">{t('mg.find.none')}</Chip>}</>}>
              <ul className="mgfind">
                {findings.length ? findings.map((f, i) => (
                  <li key={i}><span className={`pill ${f.level === 'urgent' ? 'neg' : 'crit'}`}>{t(f.level === 'urgent' ? 'mg.lvl.urgent' : 'mg.lvl.check')}</span><span>{f.text}</span></li>
                )) : <li className="muted">{t('mg.find.none')}</li>}
              </ul>
              {data.hiddenCount > 0 && <p className="muted small" style={{ margin: 0, padding: '0 18px 14px' }}>{t('mg.hidden', { n: data.hiddenCount })}</p>}
            </Panel>
          </div>

          <div className="box">
            <div className="panel-h">
              <h2>{t('mg.projects')}</h2>
              <span className="hint">{t('mg.count', { n: view.rows.filter((r) => filter === 'all' || r.status === filter).length, all: view.rows.length })}</span>
              <span className="spacer" />
              <div className="chips" role="group" aria-label={t('common.status')}>
                {(['all', 'crit', 'watch', 'ok'] as const).map((f) => (
                  <button key={f} type="button" className="chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                    {t(`mg.f.${f}`)} ({f === 'all' ? view.rows.length : view.rows.filter((r) => r.status === f).length})
                  </button>
                ))}
              </div>
            </div>
            <div className="tablewrap">
              <table>
                <thead><tr>
                  <th>{t('mg.col.project')}</th><th>{t('mg.col.customer')}</th><th className="r">{t('mg.col.hours')}</th><th className="r">{t('mg.col.ns')}</th>
                  <th className="r">{t('mg.col.cost')}</th><th className="r">{t('mg.col.margin')}</th><th className="r">{t('mg.col.pct')}</th><th className="r">{t('mg.col.target')}</th><th className="r">{t('mg.col.perHour')}</th>
                  <th>{t('mg.col.trend')}</th><th>{t('common.status')}</th>
                  <th>{t('mg.col.mix')}</th><th>{t('mg.col.what', { m: view.lastClosed ? monthName(view.lastClosed - 1) : '–' })}</th><th aria-label={t('mg.deepDive')} />
                </tr></thead>
                <tbody>
                  {view.rows.filter((r) => filter === 'all' || r.status === filter).map((r) => (
                    <tr key={r.p.workdayProjectId}>
                      <td><Link className="mglink" to={`/marge/${r.p.workdayProjectId}${year ? `?jahr=${year}` : ''}`} title={t('mg.deepDive')}>{r.p.name}</Link><div className="muted small mono">{r.p.workdayProjectId}{!r.p.project && ` · ${t('mg.noTool')}`}</div></td>
                      <td className="small">{r.p.customer || '–'}</td>
                      <td className="r num">{hrs(r.p.hoursX100)}</td>
                      <td className="r num">{eur(r.p.netSalesCents)}</td>
                      <td className="r num">{eur(r.p.costCents)}</td>
                      <td className="r num" style={r.p.netSalesCents < r.p.costCents ? { color: 'var(--neg)' } : undefined}>{eur(r.p.netSalesCents - r.p.costCents)}</td>
                      <td className="r num" style={{ fontWeight: 600, color: COLOR[r.status] }}>{r.pct === null ? '–' : p1(r.pct)}</td>
                      <td className="r num nowrap">
                        <TargetEdit value={r.p.target} fallback={target} canEdit={r.p.canSetTarget} label={t('mg.targetOf', { p: r.p.name })} id={r.p.workdayProjectId} compact
                          onSave={async (pct) => { await api('PUT', '/api/margin/targets', { workdayProjectId: r.p.workdayProjectId, pct }); reload(); }} />
                      </td>
                      <td className="r num small muted nowrap">{perHour(r.p)}</td>
                      <td><Spark series={r.series} target={r.goal} label={r.series.map((v, i) => `${monthName(i)} ${v === null ? '–' : p0(v)}`).join(', ')} /></td>
                      <td className="nowrap"><span className={`pill ${TONE[r.status]}`}>{t(`mg.state.${r.status}`)}</span>{r.falling && <div className="small" style={{ color: 'var(--crit)', marginTop: 3 }}>{t('mg.falling')}</div>}</td>
                      <td><Mix mix={r.mix} t={t} /></td>
                      <td className="mgwhat">{r.analysis ? <><span className={`pill ${SEV_TONE[r.analysis.severity]}`}>{t(`pa.sev.${r.analysis.severity}`)}</span><div className="small">{shortLine(r.analysis, getLang())}</div></> : <span className="muted">–</span>}</td>
                      <td className="r nowrap"><Link className="btn sm" to={`/marge/${r.p.workdayProjectId}${year ? `?jahr=${year}` : ''}`} aria-label={t('mg.deepDiveOf', { p: r.p.name })}>{t('mg.deepDiveBtn')}</Link></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td colSpan={2}>{t('mg.sum')}</td>
                  <td className="r num">{hrs(data.total.hoursX100)}</td><td className="r num">{eur(data.total.netSalesCents)}</td><td className="r num">{eur(data.total.costCents)}</td>
                  <td className="r num">{eur(data.total.netSalesCents - data.total.costCents)}</td><td className="r num">{p1(marginPct(data.total) ?? 0)}</td>
                  <td className="r num">{p0(target)}</td><td className="r num small muted">{perHour(data.total)}</td><td colSpan={5} />
                </tr></tfoot>
              </table>
            </div>
          </div>

          <Customers projects={data.projects} target={target} t={t} />
        </>
      )}
    </section>
  );
}

/** Zielmarge anzeigen und (wenn erlaubt) ändern; leer speichern = eigenes Ziel entfernen, dann gilt fallback. */
function TargetEdit({ value, fallback, canEdit, label, id, compact, onSave }: { value: number | null; fallback: number; canEdit: boolean; label: string; id: string; compact?: boolean; onSave: (pct: number | null) => Promise<void> }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const shown = value ?? fallback;
  const start = () => { setText(value === null ? '' : String(value).replace('.', getLang() === 'en' ? '.' : ',')); setErr(null); setEditing(true); };
  async function save(v: number | null) {
    setBusy(true); setErr(null);
    try { await onSave(v); setEditing(false); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  function submit() {
    const raw = text.trim().replace('%', '').replace(',', '.').trim();
    if (!raw) return void save(null);
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > 100) { setErr(t('mg.targetInvalid')); return; }
    void save(n);
  }
  if (editing) {
    return (
      <span className="mgtarget-edit">
        <input id={`mgt-${id}`} aria-label={label} inputMode="decimal" value={text} placeholder={p0(fallback)} autoFocus disabled={busy}
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setEditing(false); }} />
        <button className="btn primary sm" disabled={busy} onClick={submit}>{t('common.save')}</button>
        <button className="btn ghost sm" disabled={busy} onClick={() => setEditing(false)}>{t('common.cancel')}</button>
        {err && <span className="err small" role="alert">{err}</span>}
      </span>
    );
  }
  const txt = <>{p0(shown)}{value === null && compact && <span className="muted small"> {t('mg.targetArea')}</span>}{value === null && !compact && <span className="muted small"> {t('mg.targetDefault')}</span>}</>;
  return canEdit
    ? <button type="button" className="mgtarget" onClick={start} aria-label={`${label}: ${p0(shown)} – ${t('common.edit')}`} title={t('mg.targetHint')}>{txt}</button>
    : <span style={compact && value === null ? { color: 'var(--muted)' } : undefined}>{txt}</span>;
}

/** Kostenmix: kleiner gestapelter Balken und Anteile */
function Mix({ mix, t }: { mix: Record<'personnel' | 'subcontracting' | 'other', number> | null; t: T }) {
  if (!mix) return <span className="muted small">{t('mg.noCostData')}</span>;
  const total = mix.personnel + mix.subcontracting + mix.other;
  if (!total) return <span className="muted">–</span>;
  const used = COST_GROUPS.filter((g) => mix[g] / total >= 0.005);
  return (
    <div className="mgmix" title={used.map((g) => `${t(`pa.group.${g}`)} ${eur(mix[g])}`).join(', ')}>
      <span className="mgmix-bar">{used.map((g) => <i key={g} style={{ width: `${(mix[g] / total) * 100}%`, background: GROUP_COLOR[g] }} />)}</span>
      <span className="muted small">{used.map((g) => `${t(`mg.mix.${g}`)} ${p0((mix[g] / total) * 100)}`).join(' · ')}</span>
    </div>
  );
}

const perHour = (v: MarginValues) => (v.hoursX100 ? `${Math.round(v.netSalesCents / v.hoursX100)} / ${Math.round(v.costCents / v.hoursX100)} €` : '–');

function Kpis({ data, target, t }: { data: MarginOverview; target: number; t: T }) {
  const tot = data.total;
  const pct = marginPct(tot) ?? 0;
  const scale = (v: number) => `${Math.max(0, Math.min(100, (v / 70) * 100))}%`;
  return (
    <Panel id="mg.kpis" title={t('mg.kpisTitle', { y: data.year })}
      summary={<>
        <Chip>{t('mg.kpi.ns')} <b>{eur(tot.netSalesCents)}</b></Chip>
        <Chip tone={statusOf(pct, target) === 'crit' ? 'neg' : statusOf(pct, target) === 'watch' ? 'crit' : 'pos'}>{t('mg.kpi.margin')} <b>{p1(pct)}</b></Chip>
        <Chip>{t('mg.target')} {p0(target)}</Chip>
      </>}>
    <div className="kpis mgkpis">
      <div className="kpi"><div className="lbl">{t('mg.kpi.ns')}</div><div className="val num">{eur(tot.netSalesCents)}</div><div className="sub">{t('mg.kpi.nsSub', { n: data.projects.length })}</div></div>
      <div className="kpi"><div className="lbl">{t('mg.kpi.cost')}</div><div className="val num">{eur(tot.costCents)}</div><div className="sub">{t('mg.kpi.costSub')}</div></div>
      <div className="kpi">
        <div className="lbl">{t('mg.kpi.margin')}</div>
        <div className="val num">{eur(tot.netSalesCents - tot.costCents)} <span style={{ fontSize: 15, color: COLOR[statusOf(pct, target)] }}>{p1(pct)}</span></div>
        <div className="meter" title={t('mg.target')}><i style={{ width: scale(pct), background: 'var(--accent)' }} /><span className="pc" style={{ left: scale(target) }} /></div>
        <div className="sub">{t('mg.kpi.marginSub', { p: p0(target) })}</div>
      </div>
      <div className="kpi"><div className="lbl">{t('mg.kpi.hours')}</div><div className="val num">{hrs(tot.hoursX100)}</div><div className="sub">{t('mg.kpi.hoursSub', { n: hrs(tot.freeHoursX100) })}</div></div>
      <div className="kpi"><div className="lbl">{t('mg.kpi.perHour')}</div><div className="val num">{perHour(tot)}</div><div className="sub">{t('mg.kpi.perHourSub')}</div></div>
    </div>
    </Panel>
  );
}

function MonthChart({ data, target, t }: { data: MarginOverview; target: number; t: T }) {
  const closed = new Set(data.closedMonths);
  const last = Math.max(...data.closedMonths, 0);
  const shown = data.months.slice(0, Math.min(12, Math.max(last + 1, 1)));
  const max = Math.max(1, ...shown.map((v) => Math.max(v.netSalesCents, v.costCents)));
  return (
    <Panel id="mg.months" title={t('mg.months')}
      hint={<><span className="mglegend"><i style={{ background: 'var(--accent)' }} />{t('mg.legendNs')}</span> <span className="mglegend"><i style={{ background: 'var(--neu-bar)' }} />{t('mg.legendCost')}</span> · {t('mg.monthHint')}</>}
      summary={(() => { const ps = shown.map((v, i) => (closed.has(i + 1) ? marginPct(v) : null)).filter((x): x is number => x !== null); if (!ps.length) return undefined; const min = Math.min(...ps); const mi = shown.findIndex((v, i) => closed.has(i + 1) && marginPct(v) === min);
        return <><Chip>Ø <b>{p1(ps.reduce((a, b) => a + b, 0) / ps.length)}</b></Chip><Chip tone={statusOf(min, target) === 'ok' ? undefined : 'crit'}>{monthName(mi)} <b>{p1(min)}</b></Chip></>; })()}>
      <div className="mgchart" style={{ gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))` }}>
        {shown.map((v, i) => {
          const open = !closed.has(i + 1);
          const p = open ? null : marginPct(v);
          const st = statusOf(p, target);
          return (
            <div key={i} className="mgcol">
              <span className={`pill ${open ? 'neu' : TONE[st]}`}>{p === null ? '–' : p0(p)}</span>
              <div className="mgbars">
                <i title={`${t('mg.legendNs')} ${eur(v.netSalesCents)}`} style={{ height: `${(v.netSalesCents / max) * 100}%`, background: 'var(--accent)' }} />
                <i title={`${t('mg.legendCost')} ${eur(v.costCents)}`} style={{ height: `${(v.costCents / max) * 100}%`, background: 'var(--neu-bar)' }} />
              </div>
              <div className="mglbl">{monthName(i)}<div className="num">{open ? t('mg.monthOpen') : `${Math.round(v.netSalesCents / 100_000)} T€`}</div></div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function Spark({ series, target, label }: { series: (number | null)[]; target: number; label: string }) {
  return (
    <div className="mgspark" role="img" aria-label={label}>
      {series.map((v, i) => (
        <i key={i} title={label.split(', ')[i]} style={{ height: v === null ? 2 : `${Math.max(3, Math.min(70, Math.max(v, 4)) / 70 * 28)}px`, background: v === null ? 'var(--line)' : COLOR[statusOf(v, target)] }} />
      ))}
    </div>
  );
}

function Customers({ projects, target, t }: { projects: MarginProject[]; target: number; t: T }) {
  const groups = new Map<string, MarginValues>();
  for (const p of projects) {
    const g = groups.get(p.customer || '–') ?? { netSalesCents: 0, costCents: 0, hoursX100: 0, freeHoursX100: 0 };
    g.netSalesCents += p.netSalesCents; g.costCents += p.costCents; g.hoursX100 += p.hoursX100;
    groups.set(p.customer || '–', g);
  }
  const list = [...groups].sort((a, b) => b[1].netSalesCents - a[1].netSalesCents);
  return (
    <Panel id="mg.cust" title={t('mg.customers')} summary={list[0] ? <Chip>{t('mg.custN', { n: list.length })} · {list[0][0]} <b>{p1(marginPct(list[0][1]) ?? 0)}</b></Chip> : undefined}>
      <div className="mgcust">
        {list.map(([name, v]) => {
          const p = marginPct(v) ?? 0;
          const st = statusOf(p, target);
          return (
            <div key={name} className="mgcard">
              <div className="row" style={{ justifyContent: 'space-between' }}><b>{name}</b><span className="num" style={{ fontWeight: 600, color: COLOR[st] }}>{p1(p)}</span></div>
              <div className="meter"><i style={{ width: `${Math.max(0, Math.min(100, (p / 70) * 100))}%`, background: COLOR[st] }} /></div>
              <div className="muted small num">{t('mg.custLine', { ns: eur(v.netSalesCents), m: eur(v.netSalesCents - v.costCents), h: hrs(v.hoursX100) })}</div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

const ROW_TONE = { new: 'info', changed: 'crit', unchanged: 'neu' } as const;

/** Export hochladen → Probelauf mit neuen Monaten und Änderungen → übernehmen. */
function ImportPanel({ onClose, onApplied }: { onClose: () => void; onApplied: () => void }) {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<MarginImportReport | null>(null);
  const [busy, setBusy] = useState<'check' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUnchanged, setShowUnchanged] = useState(false);

  async function run(apply: boolean, f = file) {
    if (!f) return;
    setBusy(apply ? 'apply' : 'check');
    setError(null);
    try {
      const r = await upload<MarginImportReport>('POST', `/api/margin/import${apply ? '?apply=1' : ''}`, f);
      setReport(r);
      if (apply) onApplied();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }
  const pick = (f: File | undefined) => { if (!f) return; setFile(f); setReport(null); void run(false, f); };
  const todo = report ? report.counts.new + report.counts.changed : 0;
  const rows = (report?.rows ?? []).filter((r) => showUnchanged || r.state !== 'unchanged');

  return (
    <div className="box">
      <div className="panel-h"><h2>{t('mg.im.title')}</h2><span className="spacer" /><button className="btn ghost" onClick={onClose}>{t('mg.im.close')}</button></div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="muted small" style={{ margin: 0 }}>{t('mg.im.lead')}</p>
        <label className={`drop${busy ? ' busy' : ''}`} htmlFor="mgfile" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}>
          {busy === 'check' ? t('mg.im.checking') : file ? file.name : t('mg.im.drop')}
          <input id="mgfile" type="file" accept=".xlsx,.csv" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; pick(f); }} />
        </label>
        {error && <p className="err" role="alert">{error}</p>}
        {report && (
          <>
            <div className="row">
              {(['new', 'changed', 'unchanged'] as const).map((s) => <span key={s} className={`pill ${ROW_TONE[s]}`}>{t(`mg.im.state.${s}`)}: {report.counts[s]}</span>)}
              <span className="muted small">{report.newMonths.length ? t('mg.im.newMonths', { m: report.newMonths.map(ym).join(', ') }) : t('mg.im.noNewMonths')}</span>
              <span className="spacer" />
              {!report.dryRun
                ? <span className="pill pos" role="status">{t('mg.im.applied', { new: report.counts.new, changed: report.counts.changed })}</span>
                : todo > 0
                  ? <button className="btn primary" disabled={!!busy} onClick={() => run(true)}>{busy === 'apply' ? t('mg.im.applying') : t('mg.im.apply', { n: todo })}</button>
                  : <span className="muted">{t('mg.im.nothing')}</span>}
            </div>
            <p className="muted small" style={{ margin: 0 }}>
              {t('mg.im.range', { from: ym(report.firstMonth), to: ym(report.lastMonth), n: report.rows.length })}
              {report.skipped > 0 && ` · ${t('mg.im.skipped', { n: report.skipped })}`}
            </p>
            {report.unmatched.length > 0 && <p className="muted small" style={{ margin: 0 }}>{t('mg.im.unmatched', { n: report.unmatched.length, list: report.unmatched.map((u) => `${u.workdayProjectId} ${u.label}`).join(', ') })}</p>}
          </>
        )}
      </div>
      {report?.dryRun && (
        <>
          <div className="panel-h" style={{ borderTop: '1px solid var(--line)' }}>
            <span className="spacer" />
            <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} />{t('mg.im.showUnchanged')}</label>
          </div>
          <div className="tablewrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table>
              <thead><tr><th>{t('common.status')}</th><th>{t('mg.col.project')}</th><th>{t('mg.im.col.month')}</th><th className="r">{t('mg.col.ns')}</th><th className="r">{t('mg.col.cost')}</th><th className="r">{t('mg.col.hours')}</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.workdayProjectId}|${r.month}`}>
                    <td><span className={`pill ${ROW_TONE[r.state]}`}>{t(`mg.im.state.${r.state}`)}</span></td>
                    <td>{r.project?.name ?? r.label}<div className="muted small mono">{r.workdayProjectId}</div></td>
                    <td className="nowrap">{ym(r.month)}</td>
                    <Cell r={r} k="netSalesCents" fmt={eur} />
                    <Cell r={r} k="costCents" fmt={eur} />
                    <Cell r={r} k="hoursX100" fmt={hrs} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Cell({ r, k, fmt }: { r: MarginImportRow; k: keyof MarginValues; fmt: (v: number) => string }) {
  const changed = r.before && r.before[k] !== r.after[k];
  return (
    <td className="r num">
      {fmt(r.after[k])}
      {changed && <div className="small muted"><s>{fmt(r.before![k])}</s></div>}
    </td>
  );
}

/** Kostendaten hochladen → Probelauf (Projekte im Tool, übersprungene Projekte) → übernehmen. */
function CostImportPanel({ onClose, onApplied }: { onClose: () => void; onApplied: () => void }) {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<CostImportReport | null>(null);
  const [busy, setBusy] = useState<'check' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(apply: boolean, f = file) {
    if (!f) return;
    setBusy(apply ? 'apply' : 'check');
    setError(null);
    try {
      const r = await upload<CostImportReport>('POST', `/api/margin/costs/import${apply ? '?apply=1' : ''}`, f);
      setReport(r);
      if (apply) onApplied();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }
  const pick = (f: File | undefined) => { if (!f) return; setFile(f); setReport(null); void run(false, f); };
  const todo = report ? report.counts.new + report.counts.changed : 0;

  return (
    <div className="box">
      <div className="panel-h"><h2>{t('mg.ci.title')}</h2><span className="spacer" /><button className="btn ghost" onClick={onClose}>{t('mg.im.close')}</button></div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="muted small" style={{ margin: 0 }}>{t('mg.ci.lead')}</p>
        <label className={`drop${busy ? ' busy' : ''}`} htmlFor="mgcostfile" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}>
          {busy === 'check' ? t('mg.im.checking') : file ? file.name : t('mg.im.drop')}
          <input id="mgcostfile" type="file" accept=".xlsx,.csv" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; pick(f); }} />
        </label>
        {error && <p className="err" role="alert">{error}</p>}
        {report && (
          <>
            <div className="row">
              {(['new', 'changed', 'unchanged'] as const).map((s) => <span key={s} className={`pill ${ROW_TONE[s]}`}>{t(`mg.im.state.${s}`)}: {report.counts[s]}</span>)}
              <span className="muted small">{t('mg.ci.range', { from: ym(report.firstMonth), to: ym(report.lastMonth), n: report.projects.length })}</span>
              <span className="spacer" />
              {!report.dryRun
                ? <span className="pill pos" role="status">{t('mg.ci.applied', { n: report.projects.length })}</span>
                : todo > 0
                  ? <button className="btn primary" disabled={!!busy} onClick={() => run(true)}>{busy === 'apply' ? t('mg.im.applying') : t('mg.im.apply', { n: todo })}</button>
                  : <span className="muted">{t('mg.im.nothing')}</span>}
            </div>
            <p className="muted small" style={{ margin: 0 }}>{t('mg.ci.semantics')}{report.skippedRows > 0 && ` · ${t('mg.im.skipped', { n: report.skippedRows })}`}</p>
          </>
        )}
      </div>
      {report && (
        <div className="tablewrap" style={{ maxHeight: 420, overflowY: 'auto', borderTop: '1px solid var(--line)' }}>
          <table>
            <thead><tr><th>{t('mg.col.project')}</th><th>{t('common.status')}</th>{COST_GROUPS.map((g) => <th key={g} className="r">{t(`pa.group.${g}`)}</th>)}<th className="r">{t('mg.col.cost')}</th></tr></thead>
            <tbody>
              {report.projects.map((p) => {
                const rows = report.rows.filter((r) => r.workdayProjectId === p.workdayProjectId);
                const n = rows.filter((r) => r.state !== 'unchanged').length;
                return (
                  <tr key={p.workdayProjectId}>
                    <td>{p.name}<div className="muted small mono">{p.workdayProjectId}</div></td>
                    <td className="small">{n ? t('mg.ci.monthsChanged', { n, all: rows.length }) : <span className="muted">{t('mg.im.state.unchanged')}</span>}</td>
                    {COST_GROUPS.map((g) => <td key={g} className="r num">{p.byGroup[g] ? eur(p.byGroup[g]) : '–'}</td>)}
                    <td className="r num">{eur(p.totalCents)}</td>
                  </tr>
                );
              })}
              {report.skipped.map((x) => (
                <tr key={x.workdayProjectId} className="muted">
                  <td>{x.label}<div className="small mono">{x.workdayProjectId}</div></td>
                  <td colSpan={COST_GROUPS.length + 1}><span className="pill neu">{t('mg.ci.notInTool')}</span></td>
                  <td className="r num">{eur(x.totalCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
